/**
 * Is a chat row a SYSTEM-EMITTED receipt rather than something the concierge
 * actually said?
 *
 * Ingest receipts (R1 "📎 Recebi …" / R2 "✅ … processado") are written into the
 * conversation by the runtime, not composed by the model. Rendering them as
 * ordinary assistant bubbles was the alternative the ingest-flow spec's G2 gate
 * explicitly REJECTED — "the model may believe it already responded, and the
 * visual register lies" — and shipping it anyway is what this predicate exists
 * to undo. See `ai-docs/ingest-receipt-concierge-compliance-miniplan.md` §3 F1.
 *
 * ## The discriminator INVERTS our previous convention — read this before
 * ## writing any verifier against it
 *
 * Until 2026-08-03 the working signal for "deterministic line" was the ABSENCE
 * of `model`. That is exactly the shape that cost two lanes a day of
 * mis-diagnosis: an absent field is indistinguishable from a field nobody set,
 * a payload that never arrived, and a reader looking in the wrong place. B2
 * replaces it with a POSITIVE marker — `model: "gateway-injected"`,
 * `provider: "openclaw"` — and this module keys on that.
 *
 * Never re-add an `!msg.model` branch here. A row with no model is not a
 * receipt; it is a row we know nothing about.
 */

/** B2's positive marker. A receipt says what it is. */
export const GATEWAY_INJECTED_MODEL = "gateway-injected";
export const GATEWAY_INJECTED_PROVIDER = "openclaw";

/**
 * Legacy fallback: the runId namespace every receipt has always carried.
 *
 * Required because B2 stamps only rows written AFTER it rolls, while a case
 * that has been in use carries hundreds of pre-B2 rows that must keep rendering
 * correctly on reload. This is a positive marker too — a namespace that is
 * present, not a field that is missing — so it does not reintroduce
 * absence-as-signal.
 */
export const INGEST_RUN_ID_PREFIX = "lexiun-ingest:";

export interface ReceiptDiscriminatorFields {
  role: string;
  model?: string;
  provider?: string;
  runId?: string;
}

/**
 * True when the row was injected by the gateway rather than composed by the
 * model.
 *
 * `role` is part of the test on purpose: a USER turn can never be a receipt, so
 * a future runId collision on the user side cannot silently restyle the
 * lawyer's own message as a system line.
 */
export function isSystemReceipt(msg: ReceiptDiscriminatorFields): boolean {
  if (msg.role !== "assistant") return false;
  // Primary — B2's structured discriminator. Both halves required: `provider`
  // alone is not specific to receipts, and `model` alone would match a future
  // injected line from a different subsystem that deserves its own treatment.
  if (
    msg.model === GATEWAY_INJECTED_MODEL &&
    msg.provider === GATEWAY_INJECTED_PROVIDER
  ) {
    return true;
  }
  // Fallback — pre-B2 rows.
  return typeof msg.runId === "string" && msg.runId.startsWith(INGEST_RUN_ID_PREFIX);
}
